import React from 'react';
import { AccountStatement } from '@PayTrustTypes/Account';

import {
  Modal,
  Input,
  List,
  Typography,
  Select,
  Button,
  DatePicker,
  Form,
  InputNumber,
  message,
  Table
} from 'antd';
import type { SortOrder } from 'antd/es/table/interface';
import { useEffect } from 'react';

interface UserSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  status: 'Active' | 'Inactive';
}

interface UserAccount {
  id: string;
  name: string;
  accountNumber: string;
}

const { Option } = Select;

interface Props {
  open: boolean;
  onCancel: () => void;
  previewUrl: string;
  selectedDocId: string | null;
  previewFileName: string;
  searchUserTerm: string;
  isStatementFormValid: boolean;
  searchResults: UserSearchResult[];
  selectedUser: UserSearchResult | null;
  selectedAccount: UserAccount | null;
  userAccountData: {
    user: {
      accounts: UserAccount[];
    };
    accountStatements?: AccountStatement[];
  };
  isMoving: boolean;
  onSearchUser: (val: string) => void;
  onSelectUser: (user: UserSearchResult) => void;
  onSelectAccount: (accountId: string) => void;
  onMoveToUnsorted: () => void;
  onMoveToAccount: (filename?: string) => void;
  onStatementFormChange: (values: any, isValid: boolean) => void;
}

const ClientSortModal: React.FC<Props> = ({
  open,
  onCancel,
  previewUrl,
  selectedDocId,
  previewFileName,
  searchUserTerm,
  searchResults,
  selectedUser,
  selectedAccount,
  userAccountData,
  isStatementFormValid,
  isMoving,
  onSearchUser,
  onSelectUser,
  onSelectAccount,
  onMoveToUnsorted,
  onMoveToAccount: originalOnMoveToAccount,
  onStatementFormChange
}) => {
  // Function to clear user selection
  const handleClearUserSelection = () => {
    // Pass null to clear the selection
    onSelectUser(null as any); // Cast to any to bypass TypeScript check
  };
  const maskAccountNumber = (acct: string) => `••••${acct.slice(-4)}`;

  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      onStatementFormChange({}, false); // Explicitly mark as invalid
    }
  }, [open]);
  

  

  // Format date to MM/DD/YYYY
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const statementColumns = [
    {
      title: 'Statement Date',
      dataIndex: 'statementDate',
      key: 'statementDate',
      render: (text: string) => formatDate(text),
      sorter: (a: any, b: any) => new Date(a.statementDate).getTime() - new Date(b.statementDate).getTime(),
      defaultSortOrder: 'descend' as SortOrder,
      sortDirections: ['ascend', 'descend'] as SortOrder[],
    },
    {
      title: 'Payment Target Date',
      dataIndex: 'paymentTargetDate',
      key: 'paymentTargetDate',
      render: (text: string) => formatDate(text),
      sorter: (a: any, b: any) => new Date(a.paymentTargetDate).getTime() - new Date(b.paymentTargetDate).getTime(),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (text: string) => formatDate(text),
      sorter: (a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
    },
    {
      title: 'Statement Balance',
      dataIndex: 'statementBalance',
      key: 'statementBalance',
      sorter: (a: any, b: any) => a.statementBalance - b.statementBalance,
    },
    {
      title: 'Amount Due',
      dataIndex: 'amountDue',
      key: 'amountDue',
      sorter: (a: any, b: any) => a.amountDue - b.amountDue,
    },
  ];

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
  
    const requiredFields = ['statementDate', 'paymentTargetDate', 'dueDate', 'statementBalance', 'amountDue'];
  
    const hasEmpty = requiredFields.some((key) => {
      const val = values[key];
      return (
        val === undefined ||
        val === null ||
        val === '' ||
        (typeof val === 'number' && isNaN(val))
      );
    });
  
    const isValid = !hasErrors && !hasEmpty;
    onStatementFormChange(values, isValid);
  };

  // Format filename according to the required pattern
  const formatFilename = () => {
    if (!selectedAccount || !form.getFieldValue('statementDate')) return '';
    
    // Get the account provider name or use a default
    const account = userAccountData?.user?.accounts?.find(acc => acc.id === selectedAccount.id);
    const providerName = account?.name || 'unknown_provider';
    
    // Get the statement date from the form
    const statementDateValue = form.getFieldValue('statementDate');
    
    // Convert to a JavaScript Date object if it's not already
    // The DatePicker component might return a Moment.js object or similar
    const statementDate = new Date(statementDateValue);
    
    // Get the file extension from the original filename
    const extension = previewFileName.split('.').pop();
    
    // Format the filename: provider_name_MM_DD_YYYY.extension
    const formattedProviderName = providerName.replace(/\s+/g, '_');
    
    // Format the date as MM_DD_YYYY
    const month = String(statementDate.getMonth() + 1).padStart(2, '0');
    const day = String(statementDate.getDate()).padStart(2, '0');
    const year = statementDate.getFullYear();
    const formattedDate = `${month}_${day}_${year}`;
    
    return `${formattedProviderName}_${formattedDate}.${extension}`;
  };
  
  // Wrap the original onMoveToAccount to include the formatted filename
  const onMoveToAccount = () => {
    const formattedFilename = formatFilename();
    originalOnMoveToAccount(formattedFilename);
  };
  
  
  return (
    <Modal
      title="Search and Select Client"
      open={open}
      onCancel={onCancel}
      footer={null}
      width={800}
    >
      {selectedDocId && previewUrl && (
        <iframe
          src={previewUrl}
          title={previewFileName}
          style={{ width: '100%', height: 400, border: '1px solid #ccc', marginBottom: 16 }}
        />
      )}

      <Input
        placeholder="Search users by name"
        value={searchUserTerm}
        onChange={(e) => onSearchUser(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {!selectedUser ? (
        <List
          bordered
          dataSource={searchResults}
          renderItem={(user) => (
            <List.Item
              key={user.id}
              onClick={() => onSelectUser(user)}
              style={{
                cursor: 'pointer',
                opacity: user.status === 'Inactive' ? 0.5 : 1,
                fontStyle: user.status === 'Inactive' ? 'italic' : 'normal',
              }}
            >
              <span>
                {user.firstName} {user.lastName}
                <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                  ({user.status})
                </Typography.Text>
              </span>
            </List.Item>
          )}
          style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 12 }}
        />
      ) : (
        <div 
          style={{ 
            padding: '12px 16px', 
            border: '1px solid #d9d9d9', 
            borderRadius: '2px', 
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <span>
            <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
            <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
              ({selectedUser.status})
            </Typography.Text>
          </span>
          <Button 
            type="link" 
            onClick={handleClearUserSelection}
            style={{ padding: '0 8px' }}
          >
            Change
          </Button>
        </div>
      )}

      {selectedUser?.status === 'Active' && userAccountData?.user?.accounts?.length > 0 && (
        <>
          <Select
            style={{ width: '100%', marginBottom: 12 }}
            placeholder="Select an account"
            value={selectedAccount?.id}
            onChange={onSelectAccount}
            allowClear
          >
            {userAccountData.user.accounts.map((acct: UserAccount) => (
              <Option key={acct.id} value={acct.id}>
                {acct.name} — {maskAccountNumber(acct.accountNumber)}
              </Option>
            ))}
          </Select>

          {selectedAccount && (userAccountData?.accountStatements ?? []).length > 0 && (
            <>
              {(() => {
                // Filter out phantom statements (those without attachments)
                const statementsWithFiles = (userAccountData.accountStatements || []).filter(statement => 
                  statement.hasAttachment === true
                );
                
                const sortedStatements = [...statementsWithFiles].sort((a, b) => 
                  new Date(b.statementDate).getTime() - new Date(a.statementDate).getTime()
                );
                return (
                  <Table
                    dataSource={sortedStatements}
                    columns={statementColumns}
                    size="small"
                    bordered
                    pagination={{ pageSize: 50 }}
                    rowKey="id"
                    style={{ marginBottom: 16 }}
                  />
                );
              })()}
            </>
          )}

          {selectedAccount && (
            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleFormChange}
                style={{ marginTop: 16 }}
            >
              <Typography.Title level={5}>Enter Statement Information</Typography.Title>

              <Form.Item
                label="Statement Date"
                name="statementDate"
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Payment Target Date"
                name="paymentTargetDate"
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Due Date"
                name="dueDate"
                rules={[{ required: true, message: 'Required' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                label="Statement Balance"
                name="statementBalance"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
              </Form.Item>

              <Form.Item
                label="Amount Due"
                name="amountDue"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} prefix="$" />
              </Form.Item>
            </Form>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button
          type="primary"
          danger
          onClick={onMoveToUnsorted}
          disabled={!selectedUser || !!selectedAccount}
          loading={isMoving}
        >
          Move to Unsorted
        </Button>
        <Button
            type="primary"
            onClick={onMoveToAccount}
            disabled={
                !selectedUser ||
                !selectedAccount ||
                !isStatementFormValid
            }
            loading={isMoving}
            >
            Move to Account
        </Button>
      </div>
    </Modal>
  );
};

export default ClientSortModal;
